import React, { useState, useEffect } from 'react'
import {
  Modal,
  Table,
  Button,
  Badge,
  Space,
  Input,
  notification,
  Popconfirm,
  Form,
  Spin,
} from 'antd'
import {
  PlusOutlined,
  CopyOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons'
import {
  listCredentials,
  regenerateSecret,
  generateToken,
  revokeToken,
} from 'apis/sites'

/**
 * Modal for managing Etsy Extension API credentials (list, create, regenerate, revoke)
 */
const ModalManageApiKeys = ({ visible, onCancel, site }) => {
  const [loading, setLoading] = useState(false)
  const [credentials, setCredentials] = useState([])
  const [secretModal, setSecretModal] = useState({ visible: false, data: null })
  const [createModal, setCreateModal] = useState(false)
  const [createForm] = Form.useForm()

  // Fetch credentials when modal opens
  useEffect(() => {
    if (visible && site?._id) {
      fetchCredentials()
    }
  }, [visible, site])

  const fetchCredentials = async () => {
    try {
      setLoading(true)
      const res = await listCredentials(site._id)
      if (res.status === 200 && res.data.success !== false) {
        setCredentials(res.data.data || res.data || [])
      }
    } catch (err) {
      console.error('Failed to fetch credentials:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleRegenerate = async (apiKey) => {
    try {
      setLoading(true)
      const res = await regenerateSecret({ api_key: apiKey })
      if (res.status === 200 && res.data.success) {
        setSecretModal({ visible: true, data: res.data.data })
        await fetchCredentials()
      } else {
        notification.error({
          message: 'Error',
          description: res.data?.error || 'Failed to regenerate secret',
        })
      }
    } catch (err) {
      notification.error({ message: 'Error', description: 'Failed to regenerate secret' })
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields()
      setLoading(true)
      const res = await generateToken({
        site_id: site._id,
        name: values.name,
        description: values.description || '',
      })
      if (res.status === 200 && res.data.success) {
        setCreateModal(false)
        createForm.resetFields()
        setSecretModal({ visible: true, data: res.data.data })
        await fetchCredentials()
      } else {
        notification.error({
          message: 'Error',
          description: res.data?.error || 'Failed to create key',
        })
      }
    } catch (err) {
      if (err.errorFields) return // form validation error
      notification.error({ message: 'Error', description: 'Failed to create key' })
    } finally {
      setLoading(false)
    }
  }

  const handleRevoke = async (apiKey) => {
    try {
      setLoading(true)
      const res = await revokeToken({ api_key: apiKey })
      if (res.status === 200 && res.data.success) {
        notification.success({ message: 'API key revoked' })
        await fetchCredentials()
      } else {
        notification.error({
          message: 'Error',
          description: res.data?.error || 'Failed to revoke key',
        })
      }
    } catch (err) {
      notification.error({ message: 'Error', description: 'Failed to revoke key' })
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text)
    notification.success({
      message: 'Copied!',
      description: `${label} copied to clipboard`,
      duration: 2,
    })
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 180,
    },
    {
      title: 'API Key',
      dataIndex: 'api_key',
      key: 'api_key',
      width: 200,
      render: (text) => (
        <Space>
          <code style={{ fontSize: 12 }}>{text?.substring(0, 12)}...</code>
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(text, 'API Key')}
          />
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'status',
      width: 90,
      render: (active) => (
        <Badge
          status={active ? 'success' : 'error'}
          text={active ? 'Active' : 'Revoked'}
        />
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (text) => text ? new Date(text).toLocaleDateString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      }) : '-',
    },
    {
      title: 'Last Used',
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      width: 160,
      render: (text) => text ? new Date(text).toLocaleDateString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      }) : 'Never',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, record) =>
        record.is_active ? (
          <Space>
            <Popconfirm
              title="Regenerate Secret?"
              description="Old secret stops working immediately. Extension must be updated."
              onConfirm={() => handleRegenerate(record.api_key)}
              okText="Yes, Regenerate"
              okButtonProps={{ danger: true }}>
              <Button size="small" icon={<ReloadOutlined />}>
                Regenerate
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Revoke this API key?"
              description="This action cannot be undone."
              onConfirm={() => handleRevoke(record.api_key)}
              okText="Yes, Revoke"
              okButtonProps={{ danger: true }}>
              <Button size="small" danger icon={<StopOutlined />}>
                Revoke
              </Button>
            </Popconfirm>
          </Space>
        ) : (
          <span style={{ color: '#999' }}>Revoked</span>
        ),
    },
  ]

  return (
    <>
      {/* Main credentials list modal */}
      <Modal
        title={`API Keys - ${site?.name || site?.site_name || ''}`}
        visible={visible}
        onCancel={onCancel}
        width={850}
        footer={null}>
        <Spin spinning={loading}>
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModal(true)}>
              Create New Key
            </Button>
          </div>
          <Table
            columns={columns}
            dataSource={credentials}
            rowKey={(r) => r.api_key || r._id}
            size="small"
            pagination={false}
            locale={{ emptyText: 'No API keys yet. Create one to get started.' }}
          />
        </Spin>
      </Modal>

      {/* Create new key modal */}
      <Modal
        title="Create New API Key"
        visible={createModal}
        onCancel={() => {
          setCreateModal(false)
          createForm.resetFields()
        }}
        onOk={handleCreate}
        confirmLoading={loading}
        okText="Create">
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="name"
            label="Key Name"
            rules={[{ required: true, message: 'Please enter a name' }]}>
            <Input placeholder="e.g. Production Key" />
          </Form.Item>
          <Form.Item name="description" label="Description (optional)">
            <Input.TextArea rows={2} placeholder="What is this key used for?" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Secret display modal (shown after create or regenerate) */}
      <Modal
        title="API Credentials"
        visible={secretModal.visible}
        closable={false}
        maskClosable={false}
        width={600}
        footer={[
          <Button
            key="done"
            type="primary"
            onClick={() => setSecretModal({ visible: false, data: null })}>
            I have saved these credentials
          </Button>,
        ]}>
        <div
          style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: 4,
            padding: 12,
            marginBottom: 20,
          }}>
          <strong>Warning:</strong> Save this secret now! It will NOT be shown again.
        </div>

        {secretModal.data && (
          <>
            <div style={{ marginBottom: 16 }}>
              <strong>API Key:</strong>
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <Input
                  value={secretModal.data.api_key}
                  readOnly
                  style={{ fontFamily: 'monospace' }}
                />
                <Button onClick={() => copyToClipboard(secretModal.data.api_key, 'API Key')}>
                  Copy
                </Button>
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>API Secret:</strong>
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <Input.Password
                  value={secretModal.data.api_secret}
                  readOnly
                  style={{ fontFamily: 'monospace' }}
                />
                <Button onClick={() => copyToClipboard(secretModal.data.api_secret, 'API Secret')}>
                  Copy
                </Button>
              </div>
            </div>
          </>
        )}
      </Modal>
    </>
  )
}

export default ModalManageApiKeys
